import React, { useMemo, useState, useCallback } from "react";
import { v4 as uuidv4 } from "uuid";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
} from "@tanstack/react-table";
import { 
  Edit, 
  Trash, 
  ArrowUpDown, 
  Check, 
  X, 
  Loader2, 
  Plus, 
  Bot, 
  Table as TableIcon,
  CircleHelp,
  Wrench,
  Zap,
  TestTubeDiagonal,
  MountainSnow,
  Radiation,
  Dna,
  Hand,
  Earth,
  ThermometerSun,
  Dices,
  Gauge
} from "lucide-react";

import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Tooltip, TooltipContent, TooltipTrigger } from "../../components/ui/tooltip";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import MultipleSelector from "../../components/ui/MultipleSelector";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import AutoResizeTextarea from "../../components/AutoResizeTextarea";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "../../components/ui/accordion";

import { useToast } from "../../components/hooks/use-toast";
import useRiskMatrix from "../../components/hooks/useRiskMatrix";
import { useAiModel } from "../../components/hooks/useAiModel";

import {
  riskStatusColors,
  getContrastColor,
  getMatrixByName,
  calculateRiskRating,
} from "../../helpers/riskHelper";
import { mitigationStatusColors } from "../../helpers/mitigationHelper";

import { CommonDialog } from "../../components/CommonDialog";
import FormattedCell from "../../components/FormattedCell";
import MitigationCard from "../../components/MitigationCard";
import RiskBadge from "../../components/Risk Matrix/RiskBadge";
import RiskMatrixSelectionModal from "../../components/Risk Matrix/RiskMatrixSelectionModal";

import HazardModal from "./HazardModal";
import EditHazardForm from "./HazardEditor";

import firestoreService from "../../services/firestoreService";
import hazardService from "../../services/hazardService";
import functionsService from "../../services/functionsService";
import analyticsService from "../../services/analyticsService";
import aiChangeTracking from "../../tracking/AiChangeTracking";

// Custom styles for the table
const customStyles = `
  .table-container {
    width: 100%;
    height: 100%;
    overflow-x: auto; /* Enable horizontal scrolling */
    overflow-y: auto;
  }

  /* Make sure the table takes up enough width to show all columns */
  .hazard-table {
    width: max-content; /* Allow table to expand beyond container width */
    min-width: 100%; /* Ensure it fills the container at minimum */
    table-layout: fixed;
    border-collapse: separate;
    border-spacing: 0;
    font-size: 0.875rem;
  }

  .table-header {
    position: sticky;
    top: 0;
    z-index: 2;
    background-color: #f8fafc;
  }

  .hazard-table th {
    text-align: left;
    padding: 0.75rem;
    font-weight: 600;
    border-bottom: 1px solid #e2e8f0;
    color: #475569;
    background-color: #f8fafc;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
    position: relative;
  }

  .hazard-table td {
    padding: 0.75rem;
    border-bottom: 1px solid #e2e8f0;
    vertical-align: top;
  }

  .hazard-table tr:hover td {
    background-color: #f1f5f9;
  }

  .hazard-table .badge-container {
    display: flex;
    flex-wrap: wrap;
    gap: 0.25rem;
  }

  .sort-button {
    display: inline-flex;
    align-items: center;
    cursor: pointer;
  }

  .sort-button:hover {
    color: #0284c7;
  }
  
  .cause-box {
    background-color: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 0.375rem;
    padding: 0.5rem;
    margin-bottom: 0.5rem;
  }
  
  .consequence-box {
    background-color: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 0.375rem;
    padding: 0.5rem;
    margin-bottom: 0.5rem;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  }

  /* Column resizing styles */
  .resizer {
    position: absolute;
    right: 0;
    top: 0;
    height: 100%;
    width: 8px; /* Wider resize handle */
    background: transparent;
    cursor: col-resize;
    user-select: none;
    touch-action: none;
    z-index: 10;
  }

  .resizer:hover {
    background: rgba(0, 0, 0, 0.2);
  }

  .resizer.isResizing {
    background: rgba(0, 0, 0, 0.3);
    opacity: 1;
  }

  /* Make sure table allows for column resizing */
  .hazard-table {
    width: 100%;
    table-layout: fixed;
    border-collapse: separate;
    border-spacing: 0;
    font-size: 0.875rem;
  }

  /* Ensure content doesn't overflow */
  .cell-content {
    overflow: visible;
    text-overflow: ellipsis;
    word-wrap: break-word;
  }
`;

const HazardsTable = React.memo(({ project, hazards, isOwner, isEditor }) => {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [hazardToDelete, setHazardToDelete] = useState(null);
  const [hazardEditorModalOpen, setHazardEditorModalOpen] = useState(false);
  const [selectedHazard, setSelectedHazard] = useState(null);
  const [columnResizeMode, setColumnResizeMode] = useState('onChange');
  const [columnSizingInfo, setColumnSizingInfo] = useState({});
  const [editingRowId, setEditingRowId] = useState(null);
  const [editedHazard, setEditedHazard] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isRiskMatrixModalOpen, setIsRiskMatrixModalOpen] = useState(false);
  const [modalRiskType, setModalRiskType] = useState("unmitigated");

  // AI generation functions
  const [isGeneratingConsequences, setIsGeneratingConsequences] = useState(false);
  const [isGeneratingMitigations, setIsGeneratingMitigations] = useState(false);
  const [isGeneratingSfairpStatement, setIsGeneratingSfairpStatement] = useState(false);
  const [isGeneratingSafetyRequirement, setIsGeneratingSafetyRequirement] = useState(null);

  const { toast } = useToast();
  const { riskMatrix } = useRiskMatrix(project, toast);
  const [selectedModel] = useAiModel();

  // Generate consequences using AI
  const generateConsequences = async () => {
    setIsGeneratingConsequences(true);

    try {
      const { success, consequences } =
        await functionsService.generateHazardConsequences(
          project.id,
          editedHazard.description,
          editedHazard.type,
          editedHazard.hazardSource,
          editedHazard.causes,
          selectedModel
        );

      if (!success) {
        console.error("Failed to generate consequences.");

        toast({
          variant: "destructive",
          title: "Failed to generate consequences.",
        });

        return;
      }

      // Set consequences as array
      setEditedHazard((prev) => ({
        ...prev,
        consequences: [
          ...prev.consequences,
          ...consequences.map((c) => c.consequenceDescription),
        ],
      }));
    } catch (error) {
      console.error("Failed to generate consequences.", error);

      toast({
        variant: "destructive",
        title: "Failed to generate consequences.",
        description: error.message,
      });
    } finally {
      setIsGeneratingConsequences(false);
    }
  };

  // Generate mitigations using AI
  const generateMitigations = async () => {
    setIsGeneratingMitigations(true);

    try {
      const { success, mitigations } =
        await functionsService.generateHazardMitigations(
          project.id,
          editedHazard.description,
          editedHazard.type,
          editedHazard.hazardSource,
          editedHazard.causes,
          editedHazard.consequences,
          selectedModel
        );

      if (!success) {
        console.error("Failed to generate mitigations.");

        toast({
          variant: "destructive",
          title: "Failed to generate mitigations.",
        });

        return;
      }

      setEditedHazard((prev) => ({
        ...prev,
        mitigations: [...prev.mitigations, ...mitigations],
      }));
    } catch (error) {
      console.error("Failed to generate mitigations.", error);

      toast({
        variant: "destructive",
        title: "Failed to generate mitigations.",
        description: error.message,
      });
    } finally {
      setIsGeneratingMitigations(false);
    }
  };

  // Generate SFAIRP statement using AI
  const generateSfairpStatement = async () => {
    setIsGeneratingSfairpStatement(true);

    try {
      const { success, sfairpStatement } =
        await functionsService.generateHazardSFAIRP(
          project.id,
          editedHazard.description,
          editedHazard.type,
          editedHazard.hazardSource,
          editedHazard.causes,
          editedHazard.consequences,
          editedHazard.unmitigatedRisk,
          editedHazard.mitigations,
          editedHazard.retainedRisk,
          selectedModel
        );

      if (!success) {
        console.error("Failed to generate Safety statement.");

        toast({
          variant: "destructive",
          title: "Failed to generate Safety statement.",
        });
        return;
      }

      setEditedHazard((prev) => ({
        ...prev,
        sfairpStatement: prev.sfairpStatement
          ? `${prev.sfairpStatement}\n\n${sfairpStatement}`
          : sfairpStatement,
      }));
    } catch (error) {
      console.error("Failed to generate Safety statement:", error);

      toast({
        variant: "destructive",
        title: "Failed to generate Safety statement.",
        description: error.message,
      });
    } finally {
      setIsGeneratingSfairpStatement(false);
    }
  };

  // Generate safety requirement using AI
  const generateSafetyRequirement = async (index) => {
    setIsGeneratingSafetyRequirement(index);

    try {
      const { success, safetyRequirement } =
        await functionsService.generateSafetyRequirement(
          project.id,
          editedHazard.mitigations[index].description,
          selectedModel
        );

      if (!success) {
        console.error("Failed to generate safety requirement.");

        toast({
          variant: "destructive",
          title: "Failed to generate safety requirement.",
        });
        return;
      }

      handleMitigationChange(index, "safetyRequirement", safetyRequirement);
    } catch (error) {
      console.error("Failed to generate safety requirement:", error);

      toast({
        variant: "destructive",
        title: "Failed to generate safety requirement.",
        description: error.message,
      });
    } finally {
      setIsGeneratingSafetyRequirement(null);
    }
  };

  // Function to start editing a row
  const startEditing = (hazard) => {
    console.log("Starting to edit hazard:", hazard.id);
    
    // Make sure unmitigatedRisk and retainedRisk objects exist
    const hazardToEdit = { 
      ...hazard,
      unmitigatedRisk: hazard.unmitigatedRisk || { 
        probability: "", 
        severity: "", 
        rating: "" 
      },
      retainedRisk: hazard.retainedRisk || { 
        probability: "", 
        severity: "", 
        rating: "" 
      },
      causes: hazard.causes || [],
      consequences: hazard.consequences || [],
      mitigations: hazard.mitigations || [],
      users: hazard.users || [],
      projectLifecycles: hazard.projectLifecycles || [],
      locations: hazard.locations || [],
      disciplines: hazard.disciplines || [],
      systems: hazard.systems || [],
      operatingModes: hazard.operatingModes || [],
      riskCategory: hazard.riskCategory || []
    };
    
    setEditingRowId(hazard.id);
    setEditedHazard(hazardToEdit);
  };

  // Function to cancel editing
  const cancelEditing = () => {
    setEditingRowId(null);
    setEditedHazard(null);
  };

  // Function to handle field changes for the edited hazard
  const handleChange = (name, value) => {
    setEditedHazard((prev) => {
      // Update nested property
      if (name.includes(".")) {
        const [lhsName, rhsName] = name.split(".");

        // Unmitigated risk
        if (lhsName === "unmitigatedRisk") {
          return {
            ...prev,
            unmitigatedRisk: {
              ...prev.unmitigatedRisk,
              [rhsName]: value, // Update the nested property
            },
          };
        }

        // Retained risk
        else if (lhsName === "retainedRisk") {
          return {
            ...prev,
            retainedRisk: {
              ...prev.retainedRisk,
              [rhsName]: value, // Update the nested property
            },
          };
        }
      }

      // Update top-level property
      return { ...prev, [name]: value };
    });
  };

  // Function to handle mitigation changes
  const handleMitigationChange = (index, field, value) => {
    setEditedHazard((prev) => {
      const updatedMitigations = [...prev.mitigations];
      updatedMitigations[index] = {
        ...updatedMitigations[index],
        [field]: value,
      };
      return { ...prev, mitigations: updatedMitigations };
    });
  };

  // Function to save changes
  const saveChanges = async () => {
    setIsUpdating(true);

    try {
      const updatedHazard = {
        ...editedHazard,
        unmitigatedRisk: {
          ...editedHazard.unmitigatedRisk,
          rating: calculateRiskRating(
            editedHazard.unmitigatedRisk.probability,
            editedHazard.unmitigatedRisk.severity,
            riskMatrix
          ),
        },
        retainedRisk: {
          ...editedHazard.retainedRisk,
          rating: calculateRiskRating(
            editedHazard.retainedRisk.probability,
            editedHazard.retainedRisk.severity,
            riskMatrix
          ),
        },
      };

      if (editedHazard.id) {
        await hazardService.updateHazard(project, updatedHazard);
      } else {
        await hazardService.createHazard(project, updatedHazard);
      }

      setEditingRowId(null);
      setEditedHazard(null);
      
      toast({
        title: "Hazard updated successfully",
      });
    } catch (error) {
      console.error("Failed to update hazard.", error);
      toast({
        variant: "destructive",
        title: "Failed to update hazard.",
        description: error.message,
      });
    } finally {
      setIsUpdating(false);
    }
  };

  // Function to add a new cause
  const addCause = () => {
    setEditedHazard((prev) => ({
      ...prev,
      causes: [...(prev.causes || []), ""],
    }));
  };

  // Function to remove a cause
  const removeCause = (index) => {
    setEditedHazard((prev) => {
      const newCauses = prev.causes.filter((_, i) => i !== index);
      return { ...prev, causes: newCauses };
    });
  };

  // Function to add a new consequence
  const addConsequence = () => {
    setEditedHazard((prev) => ({
      ...prev,
      consequences: [...(prev.consequences || []), ""],
    }));
  };

  // Function to remove a consequence
  const removeConsequence = (index) => {
    setEditedHazard((prev) => {
      const newConsequences = prev.consequences.filter((_, i) => i !== index);
      return { ...prev, consequences: newConsequences };
    });
  };

  // Function to add a new mitigation
  const addMitigation = () => {
    setEditedHazard((prev) => ({
      ...prev,
      mitigations: [
        ...(prev.mitigations || []),
        {
          id: uuidv4(),
          mitigationId: "",
          type: "Elimination",
          description: "",
          owner: "",
          status: "Proposed",
          dueDate: "",
          safetyRequirement: "",
          evidence: "",
        },
      ],
    }));
  };

  // Function to remove a mitigation
  const removeMitigation = (index) => {
    setEditedHazard((prev) => {
      const updatedMitigations = prev.mitigations.filter((_, i) => i !== index);
      return { ...prev, mitigations: updatedMitigations };
    });
  };

  // Function to handle risk matrix selection
  const handleMatrixCellSelection = (probability, severity) => {
    if (modalRiskType === "unmitigated") {
      handleChange("unmitigatedRisk.probability", probability);
      handleChange("unmitigatedRisk.severity", severity);
    } else if (modalRiskType === "retained") {
      handleChange("retainedRisk.probability", probability);
      handleChange("retainedRisk.severity", severity);
    }
  };

  // Function to open the risk matrix modal
  const openRiskMatrixModal = (riskType) => {
    setModalRiskType(riskType);
    setIsRiskMatrixModalOpen(true);
  };

  // Helper function to render hazard type icon
  const renderHazardTypeIcon = (hazardType) => {
    let icon;

    switch (hazardType) {
      case "Mechanical":
        icon = <Wrench className="h-4 w-4 mr-2 cursor-pointer" />;
        break;
      case "Electrical":
        icon = <Zap className="h-4 w-4 mr-2 cursor-pointer" />;
        break;
      case "Chemical":
        icon = <TestTubeDiagonal className="h-4 w-4 mr-2 cursor-pointer" />;
        break;
      case "Geological":
        icon = <MountainSnow className="h-4 w-4 mr-2 cursor-pointer" />;
        break;
      case "Biological":
        icon = <Dna className="h-4 w-4 mr-2 cursor-pointer" />;
        break;
      case "Radiation":
        icon = <Radiation className="h-4 w-4 mr-2 cursor-pointer" />;
        break;
      case "Human-made":
        icon = <Hand className="h-4 w-4 mr-2 cursor-pointer" />;
        break;
      case "Environmental":
        icon = <Earth className="h-4 w-4 mr-2 cursor-pointer" />;
        break;
      case "Thermal":
        icon = <ThermometerSun className="h-4 w-4 mr-2 cursor-pointer" />;
        break;
      default:
        icon = <CircleHelp className="h-4 w-4 mr-2 cursor-pointer" />;
        break;
    }

    return (
      <Tooltip>
        <TooltipTrigger asChild>{icon}</TooltipTrigger>
        <TooltipContent>
          <p>Hazard Type: {hazardType}</p>
        </TooltipContent>
      </Tooltip>
    );
  };

  // Function to prompt for confirmation before deleting a hazard
  const confirmDelete = (hazard) => {
    setHazardToDelete(hazard);
    setDeleteDialogOpen(true);
  };

  // Function to open edit modal
  const openEditModal = (hazard) => {
    setSelectedHazard(hazard);
    setHazardEditorModalOpen(true);
  };

  // Function to delete a hazard
  const deleteHazard = useCallback(async () => {
    if (!hazardToDelete) return;

    try {
      await firestoreService.delete(
        `projects/${project.id}/hazards`,
        hazardToDelete.id
      );
      // Handle success - the UI will update automatically via the real-time subscription
    } catch (error) {
      console.error("Failed to delete hazard:", error);
      toast({
        variant: "destructive",
        title: "Failed to delete hazard.",
        description: error.message,
      });
    } finally {
      setDeleteDialogOpen(false);
      setHazardToDelete(null);
    }
  }, [project, hazardToDelete, toast]);

  const cleanGeneratedText = useCallback((text) => {
    return text ? text.replace(/##|\*\*|--/g, "").trim() : "";
  }, []);

  // This formats arrays into badge components for display
  const renderBadges = (items = []) => {
    if (!items || items.length === 0) return <span className="text-gray-400">None</span>;
    
    return (
      <div className="badge-container">
        {items.map((item, index) => (
          <Badge key={index} variant="outline" className="text-xs">
            {item}
          </Badge>
        ))}
      </div>
    );
  };

  // Define table columns - maintaining the original order from HazardsTable
  const columns = useMemo(
    () => [
      {
        id: "actions",
        header: "Actions",
        cell: ({ row }) => {
          const hazard = row.original;
          
          // If this row is being edited, show Publish/Cancel Changes buttons
          if (editingRowId === hazard.id) {
            return (
              <div className="flex flex-col space-y-2">
                <Button 
                  size="sm" 
                  className="h-8 px-2" 
                  onClick={saveChanges}
                  disabled={isUpdating}
                >
                  {isUpdating ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Check className="h-3.5 w-3.5" />
                  )}
                  <span className="ml-1">Publish Changes</span>
                </Button>
                <Button 
                  size="sm" 
                  variant="outline" 
                  className="h-8 px-2" 
                  onClick={cancelEditing}
                  disabled={isUpdating}
                >
                  <X className="h-3.5 w-3.5" />
                  <span className="ml-1">Cancel Changes</span>
                </Button>
              </div>
            );
          }
          
          // Default view with Edit and Delete buttons
          return (
            <div className="flex space-x-2">
              <Button 
                size="sm" 
                variant="outline" 
                className="h-8 px-2" 
                onClick={() => startEditing(hazard)}
              >
                <Edit className="h-3.5 w-3.5" />
              </Button>
              {(isOwner || isEditor) && (
                <Button 
                  size="sm" 
                  variant="outline" 
                  className="h-8 px-2" 
                  onClick={() => confirmDelete(hazard)}
                >
                  <Trash className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>
          );
        },
        size: 170,
        enableResizing: false,
      },
      {
        accessorKey: "hazardId",
        header: ({ column }) => (
          <div className="sort-button" onClick={() => column.toggleSorting()}>
            Hazard ID <ArrowUpDown className="ml-2 h-4 w-4" />
          </div>
        ),
        cell: ({ row }) => (
          <span className="font-medium">{row.original.hazardId}</span>
        ),
        size: 120,
        enableResizing: true,
      },
      {
        accessorKey: "riskStatus",
        header: "Risk Status",
        cell: ({ row }) => {
          const hazard = row.original;
          const status = hazard.riskStatus || "Unknown";
          const bgColor = riskStatusColors[status] || "bg-gray-100";
          
          if (editingRowId === hazard.id) {
            return (
              <Select
                value={editedHazard.riskStatus}
                onValueChange={(value) => handleChange("riskStatus", value)}
                disabled={isUpdating}
              >
                <SelectTrigger className="w-full bg-white text-xs h-8">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  {Object.keys(riskStatusColors).map((status) => (
                    <SelectItem key={status} value={status}>
                      {status}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            );
          }
          
          return (
            <Badge className={`${bgColor} border-none text-xs`}>
              {status}
            </Badge>
          );
        },
        size: 120,
        enableResizing: true,
      },
      // {
      //   accessorKey: "riskCategory",
      //   header: "Risk Category",
      //   cell: ({ row }) => {
      //     const hazard = row.original;
          
      //     if (editingRowId === hazard.id) {
      //       return (
      //         <MultipleSelector
      //           hideClearAllButton
      //           hidePlaceholderWhenSelected
      //           className="min-w-[120px]"
      //           defaultOptions={
      //             project.projectMetadata?.riskCategories?.map((category) => {
      //               return { label: category, value: category };
      //             }) || []
      //           }
      //           value={
      //             editedHazard.riskCategory?.map((category) => {
      //               return { label: category, value: category };
      //             }) || []
      //           }
      //           onChange={(value) =>
      //             handleChange(
      //               "riskCategory",
      //               value.map((x) => x.value)
      //             )
      //           }
      //           placeholder="Select categories..."
      //           disabled={isUpdating}
      //         />
      //       );
      //     }
          
      //     return renderBadges(hazard.riskCategory);
      //   },
      //   size: 150,
      //   enableResizing: true,
      // },
      {
        accessorKey: "type",
        header: "Type",
        cell: ({ row }) => {
          const hazard = row.original;
          
          if (editingRowId === hazard.id) {
            return (
              <Select
                value={editedHazard.type}
                onValueChange={(value) => {
                  const oldValue = editedHazard.type;
                  
                  // Track user edits to AI-generated hazard type
                  if (hazard.aiGenerated && oldValue !== value) {
                    aiChangeTracking.trackHazardTypeEdit(hazard, oldValue, value);
                  }
                  
                  handleChange("type", value);
                }}
                disabled={isUpdating}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select Hazard Type" />
                </SelectTrigger>
                <SelectContent>
                  { [
                    "Mechanical",
                    "Electrical",
                    "Chemical",
                    "Geological",
                    "Biological",
                    "Radiation",
                    "Human-made",
                    "Environmental",
                    "Thermal",
                  ].map((type) => (
                    <SelectItem key={type} value={type}>
                      <div className="flex items-center mr-1">
                        {renderHazardTypeIcon(type)}
                        {type}
                      </div>
                    </SelectItem>
                  ))}

                </SelectContent>
              </Select>
            );
          }
          
          return hazard.type || "-";
        },
        size: 130,
        enableResizing: true,
      },
      {
        accessorKey: "hazardSource",
        header: "Hazard Source",
        cell: ({ row }) => {
          const hazard = row.original;
          
          if (editingRowId === hazard.id) {
            return (
              <Input
                value={editedHazard.hazardSource}
                onChange={(e) => {
                  const oldValue = editedHazard.hazardSource;
                  const newValue = e.target.value;
                  
                  // Track user edits to AI-generated hazard source
                  if (hazard.aiGenerated && oldValue !== newValue) {
                    aiChangeTracking.trackHazardSourceEdit(hazard, oldValue, newValue);
                  }
                  
                  handleChange("hazardSource", newValue);
                }}
                placeholder="Example: Exposed Wires, Equipment Failure, etc."
                className="w-full text-sm"
                disabled={isUpdating}
              />
            );
          }
          
          return hazard.hazardSource || "-";
        },
        size: 150,
        enableResizing: true,
      },
      {
        accessorKey: "description",
        header: "Description/Top Event",
        cell: ({ row }) => {
          const hazard = row.original;
          
          if (editingRowId === hazard.id) {
            return (
              <Textarea
                value={editedHazard.description}
                onChange={(e) => {
                  const oldValue = editedHazard.description;
                  const newValue = e.target.value;
                  
                  // Track user edits to AI-generated hazard descriptions
                  if (hazard.aiGenerated && oldValue !== newValue) {
                    aiChangeTracking.trackHazardDescriptionEdit(hazard, oldValue, newValue);
                  }
                  
                  handleChange("description", newValue);
                }}
                placeholder="Example: Exposed wires in maintenance access panel..."
                className="w-full text-sm min-h-[80px] resize-none"
                disabled={isUpdating}
              />
            );
          }
          
          return <FormattedCell value={cleanGeneratedText(hazard.description) || "-"} />;
        },
        size: 250,
        enableResizing: true,
      },
      {
        accessorKey: "projectLifecycles",
        header: "Project Lifecycle",
        cell: ({ row }) => {
          const hazard = row.original;
          
          if (editingRowId === hazard.id) {
            return (
              <MultipleSelector
                hideClearAllButton
                hidePlaceholderWhenSelected
                className="min-w-[120px]"
                defaultOptions={
                  project.projectMetadata?.projectLifecycles?.map((lifecycle) => {
                    return { label: lifecycle, value: lifecycle };
                  }) || []
                }
                value={
                  editedHazard.projectLifecycles?.map((lifecycle) => {
                    return { label: lifecycle, value: lifecycle };
                  }) || []
                }
                onChange={(value) =>
                  handleChange(
                    "projectLifecycles",
                    value.map((x) => x.value)
                  )
                }
                placeholder="Select lifecycles..."
                disabled={isUpdating}
              />
            );
          }
          
          return renderBadges(hazard.projectLifecycles);
        },
        size: 150,
        enableResizing: true,
      },
      {
        accessorKey: "locations",
        header: "Project Location",
        cell: ({ row }) => {
          const hazard = row.original;
          
          if (editingRowId === hazard.id) {
            return (
              <MultipleSelector
                hideClearAllButton
                hidePlaceholderWhenSelected
                className="min-w-[120px]"
                defaultOptions={
                  project.projectMetadata?.locations?.map((location) => {
                    return { label: location, value: location };
                  }) || []
                }
                value={
                  editedHazard.locations?.map((location) => {
                    return { label: location, value: location };
                  }) || []
                }
                onChange={(value) =>
                  handleChange(
                    "locations",
                    value.map((x) => x.value)
                  )
                }
                placeholder="Select locations..."
                disabled={isUpdating}
              />
            );
          }
          
          return renderBadges(hazard.locations);
        },
        size: 150,
        enableResizing: true,
      },
      {
        accessorKey: "disciplines",
        header: "Discipline",
        cell: ({ row }) => {
          const hazard = row.original;
          
          if (editingRowId === hazard.id) {
            return (
              <MultipleSelector
                hideClearAllButton
                hidePlaceholderWhenSelected
                className="min-w-[120px]"
                defaultOptions={
                  project.projectMetadata?.disciplines?.map((discipline) => {
                    return { label: discipline, value: discipline };
                  }) || []
                }
                value={
                  editedHazard.disciplines?.map((discipline) => {
                    return { label: discipline, value: discipline };
                  }) || []
                }
                onChange={(value) =>
                  handleChange(
                    "disciplines",
                    value.map((x) => x.value)
                  )
                }
                placeholder="Select disciplines..."
                disabled={isUpdating}
              />
            );
          }
          
          return renderBadges(hazard.disciplines);
        },
        size: 150,
        enableResizing: true,
      },
      {
        accessorKey: "systems",
        header: "System",
        cell: ({ row }) => {
          const hazard = row.original;
          
          if (editingRowId === hazard.id) {
            return (
              <MultipleSelector
                hideClearAllButton
                hidePlaceholderWhenSelected
                className="min-w-[120px]"
                defaultOptions={
                  project.projectMetadata?.systems?.map((system) => {
                    return { label: system.name, value: system.name };
                  }) || []
                }
                value={
                  editedHazard.systems?.map((system) => {
                    return { label: system, value: system };
                  }) || []
                }
                onChange={(value) =>
                  handleChange(
                    "systems",
                    value.map((x) => x.value)
                  )
                }
                placeholder="Select systems..."
                disabled={isUpdating}
              />
            );
          }
          
          return renderBadges(hazard.systems);
        },
        size: 150,
        enableResizing: true,
      },
      {
        accessorKey: "operatingModes",
        header: "Operating Mode",
        cell: ({ row }) => {
          const hazard = row.original;
          
          if (editingRowId === hazard.id) {
            return (
              <MultipleSelector
                hideClearAllButton
                hidePlaceholderWhenSelected
                className="min-w-[120px]"
                defaultOptions={
                  project.projectMetadata?.operatingModes?.map((mode) => {
                    return { label: mode, value: mode };
                  }) || []
                }
                value={
                  editedHazard.operatingModes?.map((mode) => {
                    return { label: mode, value: mode };
                  }) || []
                }
                onChange={(value) =>
                  handleChange(
                    "operatingModes",
                    value.map((x) => x.value)
                  )
                }
                placeholder="Select operating modes..."
                disabled={isUpdating}
              />
            );
          }
          
          return renderBadges(hazard.operatingModes);
        },
        size: 150,
        enableResizing: true,
      },
      {
        accessorKey: "causes",
        header: "Causes",
        cell: ({ row }) => {
          const hazard = row.original;
          const causes = hazard.causes || [];
          
          if (editingRowId === hazard.id) {
            return (
              <div className="space-y-2">
                <div className="flex justify-end">
                  <Button 
                    size="sm" 
                    variant="outline" 
                    className="h-7 px-2" 
                    onClick={addCause}
                    disabled={isUpdating}
                  >
                    <Plus className="h-3.5 w-3.5 mr-1" />
                    Add
                  </Button>
                </div>
                
                {editedHazard.causes && editedHazard.causes.length > 0 ? (
                  editedHazard.causes.map((cause, index) => (
                    <div key={index} className="flex items-center space-x-1">
                      <Input
                        className="w-full"
                        value={cause}
                        onChange={(e) => {
                          const oldValue = cause;
                          const newValue = e.target.value;
                          const newCauses = [...editedHazard.causes];
                          newCauses[index] = newValue;
                          
                          // Track user edits to AI-generated causes
                          if (hazard.aiGenerated && oldValue !== newValue) {
                            aiChangeTracking.trackCauseEdit(hazard, oldValue, newValue);
                          }
                          
                          handleChange("causes", newCauses);
                        }}
                        placeholder="Enter cause description"
                        disabled={isUpdating}
                      />
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-8 w-8 p-0"
                        onClick={() => removeCause(index)}
                        disabled={isUpdating}
                      >
                        <Trash className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  ))
                ) : (
                  <span className="text-gray-400">No causes identified.</span>
                )}
              </div>
            );
          }
          
          return (
            <div className="space-y-1">
              {causes.length > 0 ? (
                causes.map((cause, index) => (
                  <div key={index} className="text-sm cause-box">
                    {cause}
                  </div>
                ))
              ) : (
                <span className="text-gray-400">None specified</span>
              )}
            </div>
          );
        },
        size: 250,
        enableResizing: true,
      },
      {
        accessorKey: "consequences",
        header: "Consequences",
        cell: ({ row }) => {
          const hazard = row.original;
          const consequences = hazard.consequences || [];
          
          if (editingRowId === hazard.id) {
            return (
              <div className="space-y-2">
                <div className="flex justify-end space-x-2">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button 
                        size="icon" 
                        className="h-6 w-6" 
                        onClick={generateConsequences}
                        disabled={!editedHazard.description || isGeneratingConsequences || isUpdating}
                      >
                        {isGeneratingConsequences ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <Bot className="h-3.5 w-3.5" />
                        )}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Generate consequences</p>
                    </TooltipContent>
                  </Tooltip>
                  
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button 
                        size="icon" 
                        className="h-6 w-6" 
                        onClick={addConsequence}
                        disabled={isUpdating}
                      >
                        <Plus className="h-3.5 w-3.5" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Add consequence</p>
                    </TooltipContent>
                  </Tooltip>
                </div>
                
                {editedHazard.consequences && editedHazard.consequences.length > 0 ? (
                  editedHazard.consequences.map((consequence, index) => (
                    <div key={index} className="flex items-center space-x-1">
                      <Input
                        className="w-full"
                        value={consequence}
                        onChange={(e) => {
                          const oldValue = consequence;
                          const newValue = e.target.value;
                          const newConsequences = [...editedHazard.consequences];
                          newConsequences[index] = newValue;
                          
                          // Track user edits to AI-generated consequences
                          if (hazard.aiGenerated && oldValue !== newValue) {
                            aiChangeTracking.trackConsequenceEdit(hazard, oldValue, newValue);
                          }
                          
                          handleChange("consequences", newConsequences);
                        }}
                        placeholder="Enter consequence description"
                        disabled={isUpdating}
                      />
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-8 w-8 p-0"
                        onClick={() => removeConsequence(index)}
                        disabled={isUpdating}
                      >
                        <Trash className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  ))
                ) : (
                  <span className="text-gray-400">No consequences identified.</span>
                )}
              </div>
            );
          }
          
          return (
            <div className="space-y-1">
              {consequences.length > 0 ? (
                consequences.map((consequence, index) => (
                  <div key={index} className="text-sm consequence-box">
                    {consequence}
                  </div>
                ))
              ) : (
                <span className="text-gray-400">None specified</span>
              )}
            </div>
          );
        },
        size: 250,
        enableResizing: true,
      },
      {
        accessorKey: "users",
        header: "Affected User",
        cell: ({ row }) => {
          const hazard = row.original;
          
          if (editingRowId === hazard.id) {
            return (
              <MultipleSelector
                hideClearAllButton
                hidePlaceholderWhenSelected
                className="min-w-[120px]"
                defaultOptions={
                  project.projectMetadata?.users?.map((user) => {
                    return { label: user, value: user };
                  }) || []
                }
                value={
                  editedHazard.users?.map((user) => {
                    return { label: user, value: user };
                  }) || []
                }
                onChange={(value) =>
                  handleChange(
                    "users",
                    value.map((x) => x.value)
                  )
                }
                placeholder="Select affected user(s)..."
                disabled={isUpdating}
              />
            );
          }
          
          return renderBadges(hazard.users);
        },
        size: 150,
        enableResizing: true,
      },
      {
        accessorKey: "unmitigatedRisk",
        header: "Unmitigated Risk",
        cell: ({ row }) => {
          const hazard = row.original;
          
          if (editingRowId === hazard.id) {
            return (
              <div className="flex flex-col space-y-2">
                <div className="flex items-center space-x-2">
                  <span className="text-sm font-medium">Rating:</span>
                  <RiskBadge
                    riskMatrixName={project.riskMatrix}
                    probability={editedHazard.unmitigatedRisk?.probability}
                    severity={editedHazard.unmitigatedRisk?.severity}
                  />
                </div>
                
                <div className="flex items-center">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="flex items-center space-x-1 cursor-pointer">
                        <Dices className="h-4 w-4" />
                        <Select
                          value={editedHazard.unmitigatedRisk?.probability}
                          onValueChange={(value) =>
                            handleChange("unmitigatedRisk.probability", value)
                          }
                          disabled={isUpdating}
                        >
                          <SelectTrigger className="max-w-[120px]">
                            <SelectValue placeholder="Probability" />
                          </SelectTrigger>
                          <SelectContent>
                            {riskMatrix?.probabilities?.map((prob) => (
                              <SelectItem key={prob} value={prob}>
                                {prob}
                              </SelectItem>
                            )) || []}
                          </SelectContent>
                        </Select>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Probability</p>
                    </TooltipContent>
                  </Tooltip>
                </div>
                
                <div className="flex items-center">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="flex items-center space-x-1 cursor-pointer">
                        <Gauge className="h-4 w-4" />
                        <Select
                          value={editedHazard.unmitigatedRisk?.severity}
                          onValueChange={(value) =>
                            handleChange("unmitigatedRisk.severity", value)
                          }
                          disabled={isUpdating}
                        >
                          <SelectTrigger className="max-w-[120px]">
                            <SelectValue placeholder="Severity" />
                          </SelectTrigger>
                          <SelectContent>
                            {riskMatrix?.severities?.map((sev) => (
                              <SelectItem key={sev} value={sev}>
                                {sev}
                              </SelectItem>
                            )) || []}
                          </SelectContent>
                        </Select>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Severity</p>
                    </TooltipContent>
                  </Tooltip>
                </div>
                
                <Tooltip>
                  <TooltipTrigger asChild>
                    <TableIcon
                      className="h-6 w-6 cursor-pointer transition-all duration-200 hover:text-accent-foreground hover:scale-125 focus:text-accent-foreground active:scale-95"
                      onClick={() => openRiskMatrixModal("unmitigated")}
                    />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Select from Risk Matrix</p>
                  </TooltipContent>
                </Tooltip>
              </div>
            );
          }
          
          return (
            <div className="flex flex-col space-y-1">
              <div className="flex items-center space-x-2">
                <span className="text-sm font-medium">Rating:</span>
                <RiskBadge
                  riskMatrixName={project.riskMatrix}
                  probability={hazard.unmitigatedRisk?.probability}
                  severity={hazard.unmitigatedRisk?.severity}
                />
              </div>
              <div className="text-xs">
                <span className="font-medium">P:</span> {hazard.unmitigatedRisk?.probability || "N/A"}
              </div>
              <div className="text-xs">
                <span className="font-medium">S:</span> {hazard.unmitigatedRisk?.severity || "N/A"}
              </div>
            </div>
          );
        },
        size: 150,
        enableResizing: true,
      },
      {
        accessorKey: "mitigations",
        header: "Mitigations",
        cell: ({ row }) => {
          const hazard = row.original;
          const mitigations = hazard.mitigations || [];
          
          if (editingRowId === hazard.id) {
            return (
              <div className="space-y-2">
                <div className="flex justify-end space-x-2">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button 
                        size="icon" 
                        className="h-6 w-6" 
                        onClick={generateMitigations}
                        disabled={!editedHazard.description || !editedHazard.consequences || isGeneratingMitigations || isUpdating}
                      >
                        {isGeneratingMitigations ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <Bot className="h-3.5 w-3.5" />
                        )}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Generate mitigations</p>
                    </TooltipContent>
                  </Tooltip>
                  
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button 
                        size="icon" 
                        className="h-6 w-6" 
                        onClick={addMitigation}
                        disabled={isUpdating}
                      >
                        <Plus className="h-3.5 w-3.5" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Add mitigation</p>
                    </TooltipContent>
                  </Tooltip>
                </div>
                
                {editedHazard.mitigations && editedHazard.mitigations.length > 0 ? (
                  editedHazard.mitigations.map((mitigation, index) => (
                    <div 
                      key={index} 
                      className={`${mitigationStatusColors[mitigation.status] || "bg-gray-50"} rounded p-2 text-sm space-y-2`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <Select
                          value={mitigation.type}
                          onValueChange={(value) => handleMitigationChange(index, "type", value)}
                          disabled={isUpdating}
                        >
                          <SelectTrigger className="max-w-[180px] bg-white">
                            <SelectValue placeholder="Select type" />
                          </SelectTrigger>
                          <SelectContent>
                            { [
                              "Elimination",
                              "Substitution",
                              "Isolation",
                              "Engineering",
                              "Administrative",
                              "Personal Protective Equipment",
                            ].map((type) => (
                              <SelectItem key={type} value={type}>
                                {type}
                              </SelectItem>
                            ))}

                          </SelectContent>
                        </Select>
                        
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-8 w-8 p-0"
                          onClick={() => removeMitigation(index)}
                          disabled={isUpdating}
                        >
                          <Trash className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                      
                      <Textarea
                        value={mitigation.description}
                        onChange={(e) => {
                          const newValue = e.target.value;
                          handleMitigationChange(index, "description", newValue);
                        }}
                        placeholder="Mitigation description..."
                        className="min-h-[60px] text-sm bg-white"
                        disabled={isUpdating}
                      />
                      
                      <div>
                        <div className="text-xs font-medium mb-1">Owner:</div>
                        <Select
                          value={mitigation.owner}
                          onValueChange={(value) => handleMitigationChange(index, "owner", value)}
                          disabled={isUpdating}
                        >
                          <SelectTrigger className="h-8 text-xs bg-white">
                            <SelectValue placeholder="Select owner" />
                          </SelectTrigger>
                          <SelectContent>
                            {project.projectMetadata?.mitigationOwners?.map((owner) => (
                              <SelectItem key={owner} value={owner}>
                                {owner}
                              </SelectItem>
                            )) || []}
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <div>
                        <div className="text-xs font-medium mb-1">Status:</div>
                        <Select
                          value={mitigation.status}
                          onValueChange={(value) => handleMitigationChange(index, "status", value)}
                          disabled={isUpdating}
                        >
                          <SelectTrigger className="max-w-[130px] bg-white">
                            <SelectValue placeholder="Select status" />
                          </SelectTrigger>
                          <SelectContent>
                            { [
                              "Proposed",
                              "Implemented",
                              "Transferred",
                              "Rejected",
                            ].map((status) => (
                              <SelectItem key={status} value={status}>
                                {status}
                              </SelectItem>
                            ))}

                          </SelectContent>
                        </Select>
                      </div>
                      
                      <div>
                        <div className="text-xs font-medium mb-1">Due Date:</div>
                        <Input
                          type="date"
                          value={mitigation.dueDate}
                          onChange={(e) => handleMitigationChange(index, "dueDate", e.target.value)}
                          className="h-8 text-xs bg-white"
                          disabled={isUpdating}
                        />
                      </div>
                      
                      <div>
                        <div className="text-xs font-medium mb-1">Safety Requirement:</div>
                        <div className="flex items-center">
                          <Input
                            value={mitigation.safetyRequirement}
                            onChange={(e) => handleMitigationChange(index, "safetyRequirement", e.target.value)}
                            placeholder="Safety requirement..."
                            className="text-xs bg-white"
                            disabled={isUpdating || isGeneratingSafetyRequirement === index}
                          />
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                size="icon"
                                className="h-6 w-6 ml-1"
                                variant="ghost"
                                onClick={() => generateSafetyRequirement(index)}
                                disabled={!mitigation.description || isGeneratingSafetyRequirement === index || isUpdating}
                              >
                                {isGeneratingSafetyRequirement === index ? (
                                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                ) : (
                                  <Bot className="h-3.5 w-3.5" />
                                )}
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>Generate Safety Requirement</p>
                            </TooltipContent>
                          </Tooltip>
                        </div>
                      </div>
                      
                      <div>
                        <div className="text-xs font-medium mb-1">Evidence:</div>
                        <Input
                          value={mitigation.evidence}
                          onChange={(e) => handleMitigationChange(index, "evidence", e.target.value)}
                          placeholder="Add evidence link..."
                          className="text-xs bg-white"
                          disabled={isUpdating}
                        />
                      </div>
                    </div>
                  ))
                ) : (
                  <span className="text-gray-400">No mitigations proposed.</span>
                )}
              </div>
            );
          }
          
          return (
            <div className="space-y-2">
              {mitigations.length > 0 ? (
                mitigations.map((mitigation, index) => (
                  <Accordion key={index} type="single" collapsible>
                    <AccordionItem 
                      value={mitigation.id} 
                      className={`${mitigationStatusColors[mitigation.status] || "bg-gray-50"} rounded-md overflow-hidden`}
                    >
                      <AccordionTrigger className="py-2 px-3 hover:no-underline hover:bg-opacity-80">
                        <div className="flex flex-col items-start w-full text-left">
                          <span className="font-semibold text-sm">
                            {mitigation.type || "Unknown type"}
                          </span>
                          <span className="text-xs text-gray-600">
                            {mitigation.description || "No description"}
                          </span>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="px-3 py-2 bg-white bg-opacity-90">
                        <div className="text-xs">
                          <p className="mb-1">
                            <strong>ID:</strong> {mitigation.mitigationId || "N/A"}
                          </p>
                          <p className="mb-1">
                            <strong>Owner:</strong> {mitigation.owner || "N/A"}
                          </p>
                          <p className="mb-1">
                            <strong>Status:</strong> {mitigation.status || "N/A"}
                          </p>
                          <p className="mb-1">
                            <strong>Due Date:</strong> {mitigation.dueDate
                              ? new Date(mitigation.dueDate).toLocaleDateString()
                              : "N/A"}
                          </p>
                          <p className="mb-1">
                            <strong>Safety Requirement:</strong> {mitigation.safetyRequirement || "N/A"}
                          </p>
                          <p>
                            <strong>Evidence:</strong> {mitigation.evidence ? (
                              <a 
                                href={mitigation.evidence} 
                                target="_blank" 
                                rel="noopener noreferrer" 
                                className="text-blue-500 hover:text-blue-600"
                              >
                                View Evidence
                              </a>
                            ) : "N/A"}
                          </p>
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                ))
              ) : (
                <span className="text-gray-400">No mitigations</span>
              )}
            </div>
          );
        },
        size: 350,
        enableResizing: true,
      },
      {
        accessorKey: "retainedRisk",
        header: "Retained Risk",
        cell: ({ row }) => {
          const hazard = row.original;
          
          if (editingRowId === hazard.id) {
            return (
              <div className="flex flex-col space-y-2">
                <div className="flex items-center space-x-2">
                  <span className="text-sm font-medium">Rating:</span>
                  <RiskBadge
                    riskMatrixName={project.riskMatrix}
                    probability={editedHazard.retainedRisk?.probability}
                    severity={editedHazard.retainedRisk?.severity}
                  />
                </div>
                
                <div className="flex items-center">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="flex items-center space-x-1 cursor-pointer">
                        <Dices className="h-4 w-4" />
                        <Select
                          value={editedHazard.retainedRisk?.probability}
                          onValueChange={(value) =>
                            handleChange("retainedRisk.probability", value)
                          }
                          disabled={isUpdating}
                        >
                          <SelectTrigger className="max-w-[120px]">
                            <SelectValue placeholder="Probability" />
                          </SelectTrigger>
                          <SelectContent>
                            {riskMatrix?.probabilities?.map((prob) => (
                              <SelectItem key={prob} value={prob}>
                                {prob}
                              </SelectItem>
                            )) || []}
                          </SelectContent>
                        </Select>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Probability</p>
                    </TooltipContent>
                  </Tooltip>
                </div>
                
                <div className="flex items-center">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="flex items-center space-x-1 cursor-pointer">
                        <Gauge className="h-4 w-4" />
                        <Select
                          value={editedHazard.retainedRisk?.severity}
                          onValueChange={(value) =>
                            handleChange("retainedRisk.severity", value)
                          }
                          disabled={isUpdating}
                        >
                          <SelectTrigger className="max-w-[120px]">
                            <SelectValue placeholder="Severity" />
                          </SelectTrigger>
                          <SelectContent>
                            {riskMatrix?.severities?.map((sev) => (
                              <SelectItem key={sev} value={sev}>
                                {sev}
                              </SelectItem>
                            )) || []}
                          </SelectContent>
                        </Select>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Severity</p>
                    </TooltipContent>
                  </Tooltip>
                </div>
                
                <Tooltip>
                  <TooltipTrigger asChild>
                    <TableIcon
                      className="h-6 w-6 cursor-pointer transition-all duration-200 hover:text-accent-foreground hover:scale-125 focus:text-accent-foreground active:scale-95"
                      onClick={() => openRiskMatrixModal("retained")}
                    />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Select from Risk Matrix</p>
                  </TooltipContent>
                </Tooltip>
              </div>
            );
          }
          
          return (
            <div className="flex flex-col space-y-1">
              <div className="flex items-center space-x-2">
                <span className="text-sm font-medium">Rating:</span>
                <RiskBadge
                  riskMatrixName={project.riskMatrix}
                  probability={hazard.retainedRisk?.probability}
                  severity={hazard.retainedRisk?.severity}
                />
              </div>
              <div className="text-xs">
                <span className="font-medium">P:</span> {hazard.retainedRisk?.probability || "N/A"}
              </div>
              <div className="text-xs">
                <span className="font-medium">S:</span> {hazard.retainedRisk?.severity || "N/A"}
              </div>
            </div>
          );
        },
        size: 150,
        enableResizing: true,
      },
      {
        accessorKey: "sfairpStatement",
        header: "Safety Statement",
        cell: ({ row }) => {
          const hazard = row.original;
          
          if (editingRowId === hazard.id) {
            return (
              <div className="space-y-2">
                <div className="flex justify-end mb-2">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button 
                        size="icon"
                        className="h-6 w-6"
                        onClick={generateSfairpStatement}
                        disabled={
                          isGeneratingSfairpStatement || isUpdating
                        }
                      >
                        {isGeneratingSfairpStatement ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <Bot className="h-3.5 w-3.5" />
                        )}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Generate Safety Statement</p>
                    </TooltipContent>
                  </Tooltip>
                </div>
                
                <Textarea
                  value={editedHazard.sfairpStatement}
                  onChange={(e) => {
                    const oldValue = editedHazard.sfairpStatement;
                    const newValue = e.target.value;
                    
                    // Track user edits to AI-generated SFAIRP statements
                    if (hazard.aiGenerated && hazard.aiGenerated.sfairpStatement && 
                        oldValue !== newValue) {
                      analyticsService.trackFeatureUsage('edited_ai_content', {
                        content_type: 'sfairp_statement',
                        hazard_id: hazard.id || 'new',
                        original_length: oldValue ? oldValue.length : 0,
                        new_length: newValue.length,
                        edit_distance: Math.abs((newValue.length || 0) - (oldValue ? oldValue.length : 0))
                      });
                    }
                    
                    handleChange("sfairpStatement", newValue);
                  }}
                  placeholder="Enter Safety statement..."
                  className="w-full text-sm min-h-[150px]"
                  disabled={isUpdating}
                />
              </div>
            );
          }
          
          return <FormattedCell value={cleanGeneratedText(hazard.sfairpStatement) || "-"} />;
        },
        size: 200,
        enableResizing: true,
      },
      {
        accessorKey: "residualRiskDescription",
        header: "Description of Residual Risk",
        cell: ({ row }) => {
          const hazard = row.original;
          
          if (editingRowId === hazard.id) {
            return (
              <Textarea
                value={editedHazard.residualRiskDescription}
                onChange={(e) => {
                  handleChange("residualRiskDescription", e.target.value);
                }}
                placeholder="Enter Residual Risk description..."
                className="w-full text-sm min-h-[100px]"
                disabled={isUpdating}
              />
            );
          }
          
          return <FormattedCell value={cleanGeneratedText(hazard.residualRiskDescription) || "-"} />;
        },
        size: 200,
        enableResizing: true,
      },
      // {
      //   accessorKey: "meetings",
      //   header: "Meeting",
      //   cell: ({ row }) => {
      //     const meetingNumber = row.original.meetings;
      //     if (!meetingNumber) return <span className="text-gray-400">None</span>;
          
      //     const meeting = project.projectMetadata?.meetings?.find(
      //       (m) => m.number === meetingNumber
      //     );
          
      //     return meeting ? (
      //       <div className="text-sm">
      //         {`${meeting.number} - ${new Date(meeting.date).toLocaleDateString("en-GB")}`}
      //       </div>
      //     ) : (
      //       <div className="text-sm">{meetingNumber}</div>
      //     );
      //   },
      //   size: 150,
      //   enableResizing: true,
      // },
    ],
    [project, isOwner, isEditor, cleanGeneratedText, editingRowId, editedHazard, isUpdating, riskMatrix]
  );

  const table = useReactTable({
    data: hazards,
    columns,
    columnResizeMode: 'onChange', // Setting to onChange for immediate feedback
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    enableColumnResizing: true, // Explicitly enable column resizing
    defaultColumn: {
      minSize: 80,
      maxSize: 800,
      size: 150, // Default column width
    },
    onColumnSizingChange: setColumnSizingInfo,
    state: {
      columnSizing: columnSizingInfo,
    },
  });

  return (
    <>
      <style>{customStyles}</style>
      
      <div className="table-container">
        <div className="text-xs text-right mb-2 text-gray-500 italic">
          Tip: Drag the edge of any column header to resize it
        </div>
        <table className="hazard-table">
          <thead className="table-header">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th 
                    key={header.id} 
                    style={{ 
                      width: header.getSize(), 
                      position: 'relative',
                    }}
                  >
                    <div className="cell-content">
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </div>
                    {header.column.getCanResize() && (
                      <div
                        onMouseDown={header.getResizeHandler()}
                        onTouchStart={header.getResizeHandler()}
                        className={`resizer ${
                          header.column.getIsResizing() ? 'isResizing' : ''
                        }`}
                      />
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <td 
                    key={cell.id} 
                    style={{ 
                      width: cell.column.getSize(),
                    }}
                  >
                    <div className="cell-content">
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </div>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Hazard Editor Modal */}
      <HazardModal
        isOpen={hazardEditorModalOpen}
        setIsOpen={setHazardEditorModalOpen}
        title="Edit Hazard"
      >
        {selectedHazard && (
          <EditHazardForm
            project={project}
            hazard={selectedHazard}
            setIsModalOpen={setHazardEditorModalOpen}
          />
        )}
      </HazardModal>

      {/* Delete Confirmation Dialog */}
      <CommonDialog
        open={deleteDialogOpen}
        setOpen={setDeleteDialogOpen}
        title="Delete Hazard"
        description="Are you sure you want to delete this hazard? This action cannot be undone."
        onConfirm={deleteHazard}
      />

      {/* Risk Matrix Selection Modal */}
      <RiskMatrixSelectionModal
        isOpen={isRiskMatrixModalOpen}
        setIsOpen={setIsRiskMatrixModalOpen}
        riskMatrix={riskMatrix}
        onSelectCell={handleMatrixCellSelection}
        title={`Select ${
          modalRiskType === "unmitigated" ? "Unmitigated" : "Retained"
        } Risk Values`}
        description="Click on a cell in the risk matrix to select both probability and severity values."
        riskType={modalRiskType}
      />
    </>
  );
});

export default HazardsTable;
